import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, publicApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { usePasswordReveal } from '../hooks/usePasswordReveal';
import { Loader2, Mail, Lock, ArrowRight, User, Building2, Phone, Eye, EyeOff } from 'lucide-react';

interface AppearanceSettings {
  companyName: string;
  companyTagline?: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

const defaultAppearance: AppearanceSettings = {
  companyName: 'TrackFleet',
  companyTagline: 'GPS Fleet Tracking',
  primaryColor: '#1f8da1',
  secondaryColor: '#6366f1',
  accentColor: '#f59e0b',
};

const APPEARANCE_CACHE_KEY = 'trackfleet:appearance';

function loadCachedAppearance(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(APPEARANCE_CACHE_KEY);
    if (raw) return { ...defaultAppearance, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaultAppearance;
}

export default function Register() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { t } = useTranslation();

  const [tenantName, setTenantName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordReveal = usePasswordReveal(password, setPassword);
  const confirmReveal = usePasswordReveal(confirmPassword, setConfirmPassword);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appearance, setAppearance] = useState<AppearanceSettings>(loadCachedAppearance);
  const [registrationAllowed, setRegistrationAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    publicApi.getAppearance()
      .then((data) => {
        setAppearance({ ...defaultAppearance, ...data });
        try { localStorage.setItem(APPEARANCE_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
        if (data.showRegistration === false) {
          setRegistrationAllowed(false);
          navigate('/login', { replace: true });
        } else {
          setRegistrationAllowed(true);
        }
      })
      .catch(() => {
        setRegistrationAllowed(true);
      });
  }, [navigate]);

  const tenantSlug = useMemo(() => {
    return tenantName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }, [tenantName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!consentGiven) {
      setError(t('auth.register_consent_required'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.register_password_mismatch'));
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!tenantSlug) {
      setError('Le nom de l\'entreprise est requis');
      return;
    }

    setIsLoading(true);

    try {
      // Register the tenant
      await authApi.register({
        tenantName,
        tenantSlug,
        email,
        password,
        firstName,
        lastName,
      });

      // Auto-login after registration
      const loginResponse = await authApi.login(email, password);
      const { user, accessToken } = loginResponse.data;
      setAuth(user, accessToken);

      // Redirect to onboarding
      navigate('/onboarding');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; code?: string } } };
      const code = error.response?.data?.code;
      if (code === 'REGISTRATION_DISABLED') {
        navigate('/login', { replace: true });
        return;
      } else if (code === 'SLUG_EXISTS') {
        setError('Ce nom d\'entreprise est déjà utilisé');
      } else if (code === 'EMAIL_EXISTS') {
        setError(t('auth.register_email_taken'));
      } else {
        setError(error.response?.data?.error || t('auth.register_failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const primaryColor = appearance.primaryColor;

  // Don't render the form until we know registration is allowed
  if (registrationAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (registrationAllowed === false) {
    return null;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900" />
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
          style={{ backgroundColor: `${primaryColor}33` }}
        />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-glow">
              {appearance.logo ? (
                <img src={appearance.logo} alt={appearance.companyName} className="w-full h-full object-cover" />
              ) : (
                <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{appearance.companyName}</h1>
              <p className="text-dark-400">{appearance.companyTagline || 'GPS Fleet Tracking'}</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-white mb-4">
            {t('auth.register_marketing_headline_1')}
            <br />
            <span style={{ color: primaryColor }}>{t('auth.register_marketing_headline_2')}</span>
          </h2>

          <p className="text-dark-200 text-lg max-w-md mb-8">
            {t('auth.register_marketing_subtitle')}
          </p>

          <ul className="space-y-3 text-dark-200">
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: primaryColor }}>1</span>
              {t('auth.register_step_1')}
            </li>
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: primaryColor }}>2</span>
              {t('auth.register_step_2')}
            </li>
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: primaryColor }}>3</span>
              {t('auth.register_step_3')}
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
                {appearance.logo ? (
                  <img src={appearance.logo} alt={appearance.companyName} className="w-full h-full object-cover" />
                ) : (
                  <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
                )}
              </div>
              <span className="text-2xl font-bold text-gray-900">{appearance.companyName}</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.register_title')}</h2>
            <p className="text-gray-500">{t('auth.register_subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Company name */}
              <div>
                <label htmlFor="tenantName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.company_name')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="tenantName"
                    type="text"
                    required
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.company_name_placeholder')}
                  />
                </div>
                {tenantSlug && (
                  <p className="text-gray-400 text-xs mt-1">{t('auth.tenant_id_label', { slug: tenantSlug })}</p>
                )}
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('auth.first_name')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="firstName"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                      onFocus={(e) => e.target.style.borderColor = primaryColor}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      placeholder={t('auth.first_name_placeholder')}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('auth.last_name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.last_name_placeholder')}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.email')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.email_placeholder')}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.phone')}
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.phone_placeholder')}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="password"
                    type={passwordReveal.inputProps.type}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={passwordReveal.inputProps.onChange}
                    onKeyDown={passwordReveal.inputProps.onKeyDown}
                    onBeforeInput={passwordReveal.inputProps.onBeforeInput}
                    onPaste={passwordReveal.inputProps.onPaste}
                    className="w-full pl-12 pr-12 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.password_placeholder_min8')}
                  />
                  {passwordReveal.canReveal && (
                    <button
                      type="button"
                      onClick={passwordReveal.toggle}
                      aria-label={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                      aria-pressed={passwordReveal.visible}
                      title={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    >
                      {passwordReveal.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.confirm_password_field')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="confirmPassword"
                    type={confirmReveal.inputProps.type}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={confirmReveal.inputProps.onChange}
                    onKeyDown={confirmReveal.inputProps.onKeyDown}
                    onBeforeInput={confirmReveal.inputProps.onBeforeInput}
                    onPaste={confirmReveal.inputProps.onPaste}
                    className="w-full pl-12 pr-12 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    placeholder={t('auth.confirm_password_placeholder')}
                  />
                  {confirmReveal.canReveal && (
                    <button
                      type="button"
                      onClick={confirmReveal.toggle}
                      aria-label={confirmReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                      aria-pressed={confirmReveal.visible}
                      title={confirmReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    >
                      {confirmReveal.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* GDPR consent checkbox */}
            <div className="flex items-start gap-3">
              <input
                id="consent"
                type="checkbox"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                {t('auth.consent_label_before')}{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  {t('auth.consent_link')}
                </a>{' '}
                {t('auth.consent_label_after')} <span className="text-red-500">*</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading || !consentGiven}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
              onMouseOver={(e) => !isLoading && (e.currentTarget.style.filter = 'brightness(0.9)')}
              onMouseOut={(e) => e.currentTarget.style.filter = ''}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {t('auth.create_account_button')}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.already_have_account')}{' '}
            <Link
              to="/login"
              className="font-medium transition-colors"
              style={{ color: primaryColor }}
            >
              {t('auth.login_link')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
